/**
 * SnapshotsPage — Portfolio NAV history from daily snapshots
 *
 * Line/area chart of account values over time, multi-account overlay,
 * period selector, and summary statistics.
 */
import { useEffect, useState, useCallback, useMemo } from 'react'
import {
  Camera, RefreshCw, TrendingUp, TrendingDown, Minus,
} from 'lucide-react'
import { clsx } from 'clsx'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts'
import { api } from '../lib/api'
import PageHeader from '@/components/ui/PageHeader'
import ErrorAlert from '@/components/ui/ErrorAlert'
import EmptyState from '@/components/ui/EmptyState'
import StatCard, { StatGrid } from '@/components/ui/StatCard'

// ── Types ────────────────────────────────────────────────────────────────────

interface Snapshot {
  snapshot_date: string
  account_id: string
  total_value_gbp: number
}

interface LatestSnapshot {
  account_id: string
  total_value_gbp: number
  snapshot_date: string
}

type Period = '30' | '90' | '180' | '365'

// ── Helpers ──────────────────────────────────────────────────────────────────

function gbp(n: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n)
}

function pct(n: number): string {
  return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`
}

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
  { value: '30',  label: '30d' },
  { value: '90',  label: '90d' },
  { value: '180', label: '180d' },
  { value: '365', label: '1y' },
]

// Distinct colours for multi-account overlay
const CHART_COLORS = [
  '#60a5fa', '#34d399', '#fbbf24', '#f87171', '#a78bfa',
  '#fb923c', '#2dd4bf', '#e879f9', '#94a3b8', '#38bdf8',
]

// ── Component ────────────────────────────────────────────────────────────────

export default function SnapshotsPage() {
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [latest, setLatest] = useState<LatestSnapshot[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [period, setPeriod] = useState<Period>('90')
  const [selectedAccount, setSelectedAccount] = useState<string>('__all__')

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [hRes, lRes] = await Promise.all([
        api.get(`/api/snapshots/history?days=${period}`),
        api.get('/api/snapshots/latest'),
      ])
      setSnapshots(hRes.data?.snapshots ?? [])
      setLatest(lRes.data?.snapshots ?? [])
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load snapshots')
    } finally {
      setLoading(false)
    }
  }, [period])

  useEffect(() => { fetchData() }, [fetchData])

  // Derive unique accounts
  const accountIds = useMemo(() => {
    const set = new Set<string>()
    for (const s of snapshots) set.add(s.account_id)
    for (const s of latest) set.add(s.account_id)
    return Array.from(set).sort()
  }, [snapshots, latest])

  // Build chart data: date -> { date, accountA: val, accountB: val, ... }
  const chartData = useMemo(() => {
    const accounts = selectedAccount === '__all__' ? accountIds : [selectedAccount]
    const dateMap: Record<string, Record<string, number>> = {}

    for (const s of snapshots) {
      if (!accounts.includes(s.account_id)) continue
      if (!dateMap[s.snapshot_date]) dateMap[s.snapshot_date] = {}
      dateMap[s.snapshot_date][s.account_id] = s.total_value_gbp
    }

    return Object.entries(dateMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => ({ date, ...vals }))
  }, [snapshots, selectedAccount, accountIds])

  const visibleAccounts = selectedAccount === '__all__' ? accountIds : [selectedAccount]

  // Summary stats (for the selected account(s))
  const stats = useMemo(() => {
    if (chartData.length === 0) return null

    // Sum all visible accounts per date
    const totals = chartData.map(row => {
      let sum = 0
      for (const a of visibleAccounts) sum += (row as any)[a] ?? 0
      return sum
    })

    const peak = Math.max(...totals)
    const trough = Math.min(...totals)
    const current = totals[totals.length - 1] ?? 0
    const first = totals[0] ?? 0
    const change = first > 0 ? ((current - first) / first) * 100 : 0

    return { peak, trough, current, change }
  }, [chartData, visibleAccounts])

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 animate-fade-in max-w-screen-xl mx-auto">
      <PageHeader
        title="Portfolio Snapshots"
        icon={Camera}
        subtitle="Daily NAV history"
        actions={
          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="btn-secondary gap-2 text-sm"
          >
            <RefreshCw size={14} className={clsx(loading && 'animate-spin')} aria-hidden />
            Refresh
          </button>
        }
      />

      {error && <ErrorAlert message={error} onRetry={fetchData} />}

      {/* Controls: account selector + period */}
      <div className="glass rounded-xl px-4 py-3 flex items-center gap-4 flex-wrap">
        {/* Account selector */}
        <div className="flex items-center gap-2">
          <label htmlFor="account-select" className="text-sm text-text-muted">Account</label>
          <select
            id="account-select"
            value={selectedAccount}
            onChange={e => setSelectedAccount(e.target.value)}
            className="input w-auto px-3 py-1.5"
          >
            <option value="__all__">All Accounts (overlay)</option>
            {accountIds.map(id => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
        </div>

        {/* Period selector */}
        <div className="flex items-center gap-1 bg-surface-card border border-border rounded-lg p-0.5">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPeriod(opt.value)}
              className={clsx(
                'px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
                period === opt.value
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'text-text-muted hover:text-text-secondary',
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      {stats && (
        <StatGrid cols={4}>
          <StatCard label="Peak Value" value={gbp(stats.peak)} icon={TrendingUp} size="compact"
            iconColor="text-emerald-400" valueClassName="text-emerald-400 font-mono" />
          <StatCard label="Trough" value={gbp(stats.trough)} icon={TrendingDown} size="compact"
            iconColor="text-rose-400" valueClassName="text-rose-400 font-mono" />
          <StatCard label="Current" value={gbp(stats.current)} icon={Minus} size="compact"
            iconColor="text-blue-400" valueClassName="text-blue-400 font-mono" />
          <StatCard
            label="% Change"
            value={pct(stats.change)}
            icon={stats.change >= 0 ? TrendingUp : TrendingDown}
            size="compact"
            iconColor={stats.change >= 0 ? 'text-emerald-400' : 'text-rose-400'}
            valueClassName={clsx('font-mono', stats.change >= 0 ? 'text-emerald-400' : 'text-rose-400')}
          />
        </StatGrid>
      )}

      {/* Chart */}
      <div className="card p-5">
        {loading && chartData.length === 0 ? (
          <div className="h-80 skeleton" />
        ) : chartData.length === 0 ? (
          <EmptyState
            icon={Camera}
            title="No snapshot data"
            description="No snapshots available for this period yet."
          />
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
              <defs>
                {visibleAccounts.map((acct, i) => (
                  <linearGradient key={acct} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickFormatter={v => {
                  const d = new Date(v)
                  return `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'short' })}`
                }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickFormatter={v => `${(v / 1000).toFixed(0)}k`}
                axisLine={false}
                tickLine={false}
                width={50}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  fontSize: 12,
                }}
                labelStyle={{ color: '#94a3b8' }}
                formatter={(value: number, name: string) => [gbp(value), name]}
              />
              {visibleAccounts.length > 1 && (
                <Legend
                  wrapperStyle={{ fontSize: 11, color: '#94a3b8' }}
                />
              )}
              {visibleAccounts.map((acct, i) => (
                <Area
                  key={acct}
                  type="monotone"
                  dataKey={acct}
                  name={acct}
                  stroke={CHART_COLORS[i % CHART_COLORS.length]}
                  strokeWidth={2}
                  fill={`url(#grad-${i})`}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Latest values table */}
      {latest.length > 0 && (
        <section>
          <h2 className="panel-title mb-3">Latest Account Values</h2>
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table-pro w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-text-muted text-left">
                    <th className="px-4 py-3 font-medium">Account</th>
                    <th className="px-4 py-3 font-medium !text-right">Value (GBP)</th>
                    <th className="px-4 py-3 font-medium">As of</th>
                  </tr>
                </thead>
                <tbody>
                  {latest.map(s => (
                    <tr key={s.account_id} className="border-b border-border/50 hover:bg-surface-DEFAULT/50 transition-colors">
                      <td className="px-4 py-3 font-mono font-semibold text-text-primary">{s.account_id}</td>
                      <td className="px-4 py-3 text-right font-mono text-text-primary">{gbp(s.total_value_gbp)}</td>
                      <td className="px-4 py-3 text-text-muted">{s.snapshot_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
